import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Collapse,
  Container,
  IconButton,
  InputBase,
  Pagination,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(6);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const token = useSelector((state) => state.auth.token);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function getJobs() {
      try {
        setLoading(true);
        const { status, data } = await axios.get(
          "https://le-travaille-server.onrender.com/api/jobs",
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              token: token,
            },
          },
        );
        setLoading(false);
        if (status === 200) {
          setJobs(data.items);
        } else {
          throw new Error(
            data.error || "An error occurred while fetching jobs",
          );
        }
      } catch (err) {
        setLoading(false);
        const status = err.response?.status;
        const message = err.response?.data?.error;

        if (status === 401 || message?.toLowerCase().includes("unauthorized")) {
          toast.error("Session expired! Please log in again.");
          setTimeout(() => navigate("/auth/login"), 2000);
        } else {
          setError(message || "An unknown error occurred.");
        }
      }
    }
    getJobs();
  }, [token, navigate]);

  const filteredJobs = jobs.filter((job) =>
    [job.position, job.company, job.contract, job.location]
      .join(" ")
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const NoResults = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 1,
        px: 2,
        gap: 1,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, color: "#00786F" }}>
        No jobs found!
      </Typography>

      <Box
        sx={{
          width: { xs: 160, sm: 220, md: 260 },
          height: { xs: 120, sm: 160, md: 180 },
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src="/no_record.svg"
          alt="No jobs found"
          style={{
            width: "100%",
            height: "140%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: "center",
          maxWidth: 400,
          marginBottom: "2rem",
        }}
      >
        We couldn't find any jobs matching "{searchQuery}". Try adjusting your
        search terms or browse all available positions.
      </Typography>

      {searchQuery && (
        <Button
          variant="outlined"
          onClick={() => setSearchQuery("")}
          sx={{
            borderColor: "#00786F",
            color: "#00786F",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#00786F", color: "#fff" },
          }}
        >
          Clear Search
        </Button>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ pt: 0 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom color="#00786F">
        Job Listings
      </Typography>

      <Paper
        elevation={isFocused || searchQuery ? 1 : 3}
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          mb: 4,
          borderRadius: 3,
          transition: "all 0.25s ease",
          transform:
            isFocused || searchQuery
              ? "translateY(2px) scale(0.995)"
              : "translateY(0) scale(1)",
          boxShadow:
            isFocused || searchQuery
              ? "inset 0 4px 10px rgba(0,0,0,0.15)"
              : "0px 4px 12px rgba(0,0,0,0.12)",
          backgroundColor: isFocused || searchQuery ? "#f8f9fa" : "#ffffff",
          border: isFocused ? "2px solid #00786F" : "2px solid transparent",
        }}
      >
        <SearchIcon
          sx={{
            mx: 1,
            color: isFocused || searchQuery ? "#00786F" : "text.secondary",
            transition: "all 0.2s ease",
          }}
        />

        <InputBase
          placeholder="Search jobs..."
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            mr: 1,
            "& input::placeholder": {
              opacity: 0.7,
            },
          }}
        />

        {searchQuery && (
          <IconButton
            onClick={() => setSearchQuery("")}
            size="small"
            sx={{
              color: "#00786F",
              "&:hover": {
                backgroundColor: "#CBFBF1",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>

      <Collapse in={!!error}>
        <Alert
          severity="error"
          action={
            <IconButton size="small" onClick={() => setError("")}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      </Collapse>

      {loading ? (
        <Grid container spacing={4} justifyContent="center">
          {Array.from({ length: jobsPerPage }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card elevation={4} sx={{ borderRadius: 3, p: 3, width: "100%" }}>
                <CardContent>
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mt: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={18}
                    sx={{ mt: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={40}
                    sx={{ mt: 2, borderRadius: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : currentJobs.length > 0 ? (
        <Grid container spacing={4} justifyContent="center">
          {currentJobs.map((job) => (
            <Grid item xs={12} sm={6} md={4} key={job.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card
                  elevation={4}
                  sx={{ borderRadius: 3, p: 3, width: "100%" }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {job.position}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                      {job.company}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {job.location} • {job.contract}
                    </Typography>
                    <Typography>
                      Deadline:{" "}
                      {job.expiryDate
                        ? new Date(job.expiryDate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "Not Specified"}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: "#00786F",
                        fontWeight: "bold",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                        transition: "transform 0.2s ease-in-out",
                        mt: 2,
                      }}
                      onClick={() => {
                        if (job.link) {
                          window.open(job.link, "_blank");
                        }
                      }}
                    >
                      Apply Now
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <NoResults />
      )}

      {filteredJobs.length > jobsPerPage && (
        <Pagination
          count={Math.ceil(filteredJobs.length / jobsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "center",
            "& .MuiPaginationItem-root": {
              color: "#00786F",
            },
            "& .MuiPaginationItem-root:hover": {
              backgroundColor: "rgba(0, 120, 111, 0.08)",
            },
            "& .Mui-selected": {
              backgroundColor: "#00786F !important",
              color: "#fff !important",
              "&:hover": {
                backgroundColor: "#005b54 !important",
              },
            },
            "& .MuiPaginationItem-ellipsis": {
              color: "#00786F",
            },
          }}
        />
      )}
    </Container>
  );
}

export default Jobs;
